import { Injectable } from '@angular/core';
import {
  DocumentEditorActionsType,
  DocumentEditorNotification,
  DocumentEditorNotificationScope,
  DocumentEditorNotificationType,
  DocumentInfo,
  TemplateAction,
  TemplateActionArgs,
  UiWidget,
} from '@pt/pt-models';
import { WidgetType } from 'it/primatoo/openapi/api/org/schemas/common/docs/WidgetType';

import { ChapterSelector } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterSelector';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { WidgetUtils } from '../widgets/widget.utils';
import * as NgrxWidgetActions from '../store/actions/widget.actions';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { WidgetContactChooserValueV0Dto } from '../widgets/widget-contact-chooser/v0/models/widget-contact-chooser-value-v0.dto';
import { WRAQueryGlobalMeasureCheckValue } from '../widgets/widget-risk-assessment/v0/models/widget-risk-assessment-v0-args.dto';
import { WRAValueAnswer } from '../widgets/widget-risk-assessment/v0/models/wraValueAnswer';
import { WraValue } from '../widgets/widget-risk-assessment/v0/models/wraValue';
import { WidgetActionsUtils } from '../utils';
import {
  ApplicationService,
  BusinessProjectManagementService,
  DownloadService,
  LogService,
  QueryService,
} from '@pt/api-client';
import { WidgetDataRetentionAndDeletionV0ValueDto } from '../widgets/widget-data-retention-and-deletion/v0/models/widget-data-retention-and-deletion-v0-value.dto';
import { WidgetPreliminaryRiskAssessmentValueV0 } from '../widgets/widget-preliminary-risk-assessment/v0/models/widget-preliminary-risk-assessment-value-v0';
import { Application } from 'it/primatoo/openapi/api/org/schemas/application/Application';
import {
  WidgetAppSummaryV0ValueAppDto,
  WidgetAppSummaryV0ValueDto,
} from '../widgets/widget-app-summary/v0/models/widget-app-summary-v0-value-app.dto';
import { AssessmentCategory } from 'it/primatoo/openapi/api/org/schemas/appassessment/AssessmentCategory';
import { catchError, map } from 'rxjs/operators';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { WidgetRecipientChooserV0Value } from '../widgets/widget-recipient-chooser/v0/models/widget-recipient-chooser-v0-preview.dto';
import { EditorStoreService } from './editor-store.service';
import { Widget } from 'it/primatoo/openapi/api/org/schemas/common/docs/Widget';
import { WidgetTreatmentDataInterestedV0Value } from '../widgets/widget-treatment-data-interested/v0/models/widget-treatment-data-interested-v0-value';
import { WidgetPreliminaryRiskAssessmentTriggerArgsV0 } from '../widgets/widget-preliminary-risk-assessment/v0/models/widget-preliminary-risk-assessment-trigger-args-v0';
import { LogLevel } from 'it/primatoo/openapi/api/common/schemas/LogLevel';
import { WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs } from '../widgets/widget-checkbox/v0/models/widget-preliminary-risk-assessment-from-checkbox-trigger-args-v0';

@Injectable({
  providedIn: 'root',
})
export class TriggerService {
  constructor(
    private translateService: TranslateService,
    private queryService: QueryService,
    private downloadService: DownloadService,
    private applicationService: ApplicationService,
    private triggerStoreService: EditorStoreService,
    private logService: LogService,
    private store: Store
  ) {}

  private getWidgetValue(widgets: UiWidget[], internalId: number): any {
    const widget = widgets?.find((w) => w.internalId === internalId);
    return widget && widget.value ? widget.value : undefined;
  }

  private validWidgetAction(
    widget: UiWidget,
    action: DocumentEditorActionsType
  ) {
    const data: DocumentEditorNotification = {
      id: WidgetUtils.generateId('validWidgetAction_'),
      name: widget.label,
      action,
      widgetInternalId: widget.internalId,
      type: DocumentEditorNotificationType.INFO,
      message: this.translateService.stream('WIDGET_WARNING.SUPPORTED_ACTION'),
      timestamp: Date.now(),
      scope: DocumentEditorNotificationScope.WIDGET_ACTION,
      table: null,
      uuid: null,
    };
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  public execSyncTrigger(
    syncAction: TemplateAction[],
    sourceWidget: UiWidget,
    docInfo: DocumentInfo,
    activeChapterInfo: Chapter
  ): Observable<Widget[]> {
    return new Observable((observer) => {
      const actionIndexWithQuery = [];
      const actionIndexWithoutQuery = [];
      const observableQuery: Observable<any>[] = [];
      syncAction.forEach((action: TemplateAction, index: number) => {
        if (action?.args?.query) {
          observableQuery.push(this.queryService.resolve(action.args.query));
          actionIndexWithQuery.push(index);
        } else {
          actionIndexWithoutQuery.push(index);
        }
      });

      const WidgetRequest$: Observable<Widget>[] = [];
      /**
       * Eseguo i widget senza query e li aggiungo ai widget modificati che andranno salvati
       */
      actionIndexWithoutQuery.forEach((i) => {
        const currentTemplateAction: TemplateAction = syncAction[
          i
        ] as TemplateAction;
        const targetWidget =
          this.triggerStoreService.currentActiveChapterWidgets?.find(
            (w) => w.internalId === currentTemplateAction.widgetId
          );
        if (targetWidget && targetWidget.type) {
          const tw: UiWidget = this.triggerStoreService.getWidget(
            currentTemplateAction.widgetId
          );
          WidgetRequest$.push(
            this.execTrigger(
              sourceWidget,
              tw,
              currentTemplateAction.action,
              currentTemplateAction.args,
              activeChapterInfo.selector,
              activeChapterInfo.selector
            )
          );
        } else {
          // console.warn('Trigger: Missing Target Widget');
          const tw: UiWidget = this.triggerStoreService.getWidget(
            currentTemplateAction.widgetId
          );
          WidgetRequest$.push(
            this.execTrigger(
              sourceWidget,
              tw,
              currentTemplateAction.action,
              currentTemplateAction.args,
              activeChapterInfo.selector,
              activeChapterInfo.selector
            )
          );
        }
      });

      if (observableQuery.length > 0) {
        /**
         * Eseguo i widget che hanno query
         */
        forkJoin([
          ...observableQuery.map((query) =>
            query.pipe(
              map((response) => {
                return response;
              }),
              catchError(() => {
                this.notificationTriggerFailed(sourceWidget, docInfo.name);
                return EMPTY;
              })
            )
          ),
        ]).subscribe(
          (response) => {
            /**
             * Aggiorno i valori dei widget con query e li aggiungo ai widget modificati <changedWidget>
             */
            response?.forEach((queryResponse, index) => {
              if (queryResponse) {
                const actionIndex = actionIndexWithQuery[index];
                const currentTemplateAction: TemplateAction = syncAction[
                  actionIndex
                ] as TemplateAction;
                const targetWidget: UiWidget =
                  this.triggerStoreService.currentActiveChapterWidgets?.find(
                    (w) => w.internalId === currentTemplateAction.widgetId
                  );
                WidgetRequest$.push(
                  this.execTrigger(
                    sourceWidget,
                    targetWidget,
                    currentTemplateAction.action,
                    currentTemplateAction.args,
                    activeChapterInfo.selector,
                    activeChapterInfo.selector,
                    queryResponse
                  )
                );
              }
            });
            /**
             * Salvo le modifiche frutto di widget senza query + widget con query + widget modificato dall'utente
             */
            this.notificationTriggerSuccess(null, docInfo.name);
            forkJoin(WidgetRequest$).subscribe(
              (success) => {
                observer.next(success);
                observer.complete();
              },
              () => {
                observer.next(undefined);
                observer.complete();
              }
            );
            // this.setDisableWarning(resetWarning)
            // this.store.dispatch(NgrxWidgetActions.saveWidgets({widgets: changedWidget, resetWarning}));
          },
          () => {
            /**
             * Qualcosa non è andato nelle query, salvo i widget cambiati che sono quelli senza query + widget modificato dall'utente
             */
            this.notificationTriggerFailed(null, docInfo.name);
            observer.next(undefined);
            observer.complete();
            // this.store.dispatch(NgrxWidgetActions.saveWidgets({widgets: changedWidget, resetWarning}));
          }
        ); // END forkJoin
      } else {
        /**
         * Salvo le modifiche frutto dei SOLI widget senza query + widget modificato dall'utente
         */
        this.notificationTriggerSuccess(null, docInfo.name);
        forkJoin(WidgetRequest$).subscribe(
          (success) => {
            observer.next(success);
            observer.complete();
          },
          () => {
            observer.next(undefined);
            observer.complete();
          }
        );
        // this.store.dispatch(NgrxWidgetActions.saveWidgets({widgets: changedWidget, resetWarning}));
      }
    });
  }

  public execAsyncTrigger(
    asyncAction: TemplateAction[],
    sourceWidget: UiWidget,
    docInfo: DocumentInfo,
    activeChapterInfo: Chapter,
    chapters: Chapter[]
  ): Observable<Widget[]> {
    return new Observable((observer) => {
      const actionIndexWithQuery = [];
      const actionIndexWithoutQuery = [];
      const observableQuery: Observable<any>[] = [];
      asyncAction.forEach((action: TemplateAction, index: number) => {
        if (action?.args?.query) {
          observableQuery.push(this.queryService.resolve(action.args.query));
          actionIndexWithQuery.push(index);
        } else {
          actionIndexWithoutQuery.push(index);
        }
      });

      if (observableQuery.length > 0) {
        //Eseguo i widget che hanno query
        forkJoin([
          ...observableQuery.map((query) =>
            query.pipe(
              map((response) => {
                return response;
              }),
              catchError(() => {
                this.notificationTriggerFailed(sourceWidget, docInfo.name);
                return undefined;
              })
            )
          ),
        ]).subscribe(
          (response) => {
            const WidgetRequest: Observable<Widget>[] = [];
            //Aggiorno i valori dei widget con query e li aggiungo ai widget modificati <changedWidget>
            response?.forEach((queryResponse, index) => {
              if (queryResponse) {
                const actionIndex = actionIndexWithQuery[index];
                const currentTemplateAction: TemplateAction = asyncAction[
                  actionIndex
                ] as TemplateAction;
                const selectedChapterId = chapters?.find(
                  (c) => c.selector === currentTemplateAction.chapterSelector
                )?.id;
                WidgetRequest.push(
                  this.execAsyncTriggerWithQueryResponse(
                    currentTemplateAction,
                    docInfo,
                    selectedChapterId,
                    sourceWidget,
                    activeChapterInfo.selector,
                    queryResponse
                  )
                );
              }
            });
            forkJoin(WidgetRequest).subscribe((WidgetRequestProcessed) => {
              const response: Widget[] = [];
              WidgetRequestProcessed.forEach((wur) => {
                if (wur) {
                  response.push(wur);
                }
              });
              observer.next(response);
              observer.complete();
            });
          },
          () => {
            observer.next(undefined);
            observer.complete();
          }
        ); // END forkJoin
      } else {
        const WidgetRequest: Observable<Widget>[] = [];
        // Eseguo i widget senza query e li aggiungo ai widget modificati che andranno salvati
        actionIndexWithoutQuery.forEach((i) => {
          const currentTemplateAction: TemplateAction = asyncAction[
            i
          ] as TemplateAction;
          const selectedChapterId = chapters?.find(
            (c) => c.selector === currentTemplateAction.chapterSelector
          )?.id;
          WidgetRequest.push(
            this.execAsyncTriggerWithQueryResponse(
              currentTemplateAction,
              docInfo,
              selectedChapterId,
              sourceWidget,
              activeChapterInfo.selector
            )
          );
          forkJoin(WidgetRequest).subscribe((WidgetRequestProcessed) => {
            const response: Widget[] = [];
            WidgetRequestProcessed.forEach((wur) => {
              if (wur) {
                response.push(wur);
              }
            });
            observer.next(response);
            observer.complete();
          });
        });
      }
    });
  }

  private execAsyncTriggerWithQueryResponse(
    currentTemplateAction: TemplateAction,
    docInfo: DocumentInfo,
    selectedChapterId: number,
    sourceWidget: UiWidget,
    activeChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget> {
    return new Observable((observer) => {
      const service: BusinessProjectManagementService =
        this.downloadService.getDocumentEditorTypeService(docInfo.docType);
      if (selectedChapterId) {
        service
          .getTreatmentTemplateChapterId(docInfo.docId, selectedChapterId)
          .subscribe((chapter) => {
            this.triggerStoreService.updateChapterInfo(chapter);

            const targetWidgets =
              WidgetActionsUtils.getWidgetFromTemplateGroups(
                chapter.payload?.groups
              );
            const targetWidget = targetWidgets?.find(
              (w) => w.internalId === currentTemplateAction.widgetId
            );
            const targetChapterSelector = chapter.payload?.selector;
            this.execTriggerWithCachedWidget(
              sourceWidget,
              targetWidget,
              targetWidgets,
              currentTemplateAction.action,
              currentTemplateAction.args,
              activeChapterSelector,
              targetChapterSelector,
              queryResponse
            ).subscribe(
              (success) => {
                observer.next(success);
                observer.complete();
              },
              () => {
                observer.next(undefined);
                observer.complete();
              }
            );
          });
      } else {
        observer.next(undefined);
        observer.complete();
      }
    });
  }

  /**
   * ATTENZIONE QUI NON USARE la lista di WIDET logale perchè relativa al capitolo corrente
   */

  private notificationTriggerSuccess(widget: UiWidget, name: string) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      null,
      this.translateService.stream('WIDGET_WARNING.TRIGGER_SUCCESS'),
      DocumentEditorNotificationType.INFO,
      DocumentEditorNotificationScope.TRIGGER
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  private notificationTriggerFailed(widget: UiWidget, name: string) {
    const data = WidgetUtils.generateWidgetNotification(
      widget ? widget.internalId : 0,
      name,
      null,
      this.translateService.stream('WIDGET_WARNING.TRIGGER_FAILED'),
      DocumentEditorNotificationType.ERROR,
      DocumentEditorNotificationScope.TRIGGER
    );
    this.store.dispatch(
      NgrxWidgetActions.emitDocumentEditorNotification({ data })
    );
  }

  private execTriggerWithCachedWidget(
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    widgets: UiWidget[],
    action: DocumentEditorActionsType,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector,
    targetChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    /*
      Tengo aggiornata la lista dei widget. Necessario per i json path
    */
    switch (action) {
      case DocumentEditorActionsType.PRELIMINARY_RISK_FROM_CHECKBOX:
        return this.preliminaryRiskFromCheckbox(
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.PRELIMINARY_RISK_FROM_TREATMENT_DATA_INTERESTED:
        return this.preliminaryRiskFromTreatmentDataInterested(
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.INITIALIZATION:
        return this.initialization(
          widgets,
          sourceWidget,
          args,
          sourceChapterSelector,
          queryResponse
        );
      case DocumentEditorActionsType.ALIGN_CATEGORY:
        return this.alignCategory(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ACTIVE_ACTION:
        return this.activeAction(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector,
          queryResponse
        );
      case DocumentEditorActionsType.DEACTIVATE_ACTION:
        return this.disableAction(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector,
          queryResponse
        );
      case DocumentEditorActionsType.UPSERT_VALUE:
        return this.upsertValue(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      case DocumentEditorActionsType.APP_VALUE:
        return this.appValue(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.UPSERT_DATA_RETENTION_AND_DELETION:
        return this.upsertDataRetentionAndDeletion(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ALIGN_RESPONSIBLE_ME:
        return this.alignResponsibleMe(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ALIGN_RESPONSIBLE:
        return this.alignResponsible(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ALIGN_REPRESENTATIVE_OF_RESPONSIBLE:
        return this.alignRepresentativeOfResponsible(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ALIGN_HOLDER:
        return this.alignHolder(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ALIGN_REPRESENTATIVE_OF_HOLDER:
        return this.alignRepresentativeOfHolder(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ALIGN_SUB_RESPONSIBLE:
        return this.alignSubResponsible(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      case DocumentEditorActionsType.ENABLE_LIA:
        return this.enableLia(sourceWidget);

      default:
        console.error('Missing trigger action ', action);
        return of(undefined);
    }
  }

  public execTrigger(
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    action: DocumentEditorActionsType,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector,
    targetChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    /*
      Tengo aggiornata la lista dei widget. Necessario per i json path
    */
    this.triggerStoreService.upsertWidget(sourceWidget);
    return this.execTriggerWithCachedWidget(
      sourceWidget,
      targetWidget,
      this.triggerStoreService.currentActiveChapterWidgets,
      action,
      args,
      sourceChapterSelector,
      targetChapterSelector,
      queryResponse
    );
  }

  private preliminaryRiskFromTreatmentDataInterested(
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.TREATMENT_DATA_INTERESTED:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.PRELIMINARY_RISK_FROM_TREATMENT_DATA_INTERESTED
        );
        return this.preliminaryRiskFromTreatmentDataInterestedImpl(
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private preliminaryRiskFromCheckbox(
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.CHECKBOX:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.PRELIMINARY_RISK_FROM_CHECKBOX
        );
        return this.preliminaryRiskFromCheckboxImpl(
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private preliminaryRiskFromTreatmentDataInterestedImpl(
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const sourceValue: WidgetTreatmentDataInterestedV0Value =
      sourceWidget.value;
    const targetValue: WidgetPreliminaryRiskAssessmentValueV0[] =
      targetWidget.value;
    const triggerArgs: WidgetPreliminaryRiskAssessmentTriggerArgsV0[] =
      args as WidgetPreliminaryRiskAssessmentTriggerArgsV0[];

    if (this.logService.isLoggable(LogLevel.DEBUG)) {
      console.groupCollapsed('preliminaryRiskFromTreatmentDataInterestedImpl');
      console.log('SourceValue: ', sourceValue);
      console.log('TargetValue: ', targetValue);
      console.log('Args:   ', triggerArgs);
      console.groupEnd();
    }

    let outputTargetValue: WidgetPreliminaryRiskAssessmentValueV0[] =
      targetValue || [];

    triggerArgs.forEach((a: WidgetPreliminaryRiskAssessmentTriggerArgsV0) => {
      if (a.info && this.logService.isLoggable(LogLevel.DEBUG)) {
        console.log('------------------------');
        console.log(a.info);
      }

      switch (a.sourceOperator) {
        case 'includeOneOrMoreRow':
          outputTargetValue = TriggerService.includeOneOrMoreRow(
            sourceValue.row,
            a.sourceValue?.row || [],
            a,
            outputTargetValue,
            this.logService.isLoggable(LogLevel.DEBUG)
          );
          break;
        case 'notIncludeRow':
          outputTargetValue = TriggerService.notIncludeRow(
            sourceValue.row,
            a.sourceValue?.row,
            a,
            outputTargetValue,
            this.logService.isLoggable(LogLevel.DEBUG)
          );
          break;
        case 'includeOneOrMoreCol':
          outputTargetValue = TriggerService.includeOneOrMoreCol(
            sourceValue.col,
            a.sourceValue?.col || [],
            a,
            outputTargetValue,
            this.logService.isLoggable(LogLevel.DEBUG)
          );
          break;
        case 'notIncludeCol':
          outputTargetValue = TriggerService.notIncludeCol(
            sourceValue.col,
            a.sourceValue?.col || [],
            a,
            outputTargetValue,
            this.logService.isLoggable(LogLevel.DEBUG)
          );
          break;
        default:
          if (this.logService.isLoggable(LogLevel.DEBUG)) {
            console.log(
              'Ignored Trigger: INVALID sourceOperator ' + a.sourceOperator
            );
          }
          return of(undefined);
      }
    });

    const request: Widget = {
      editable: targetWidget.editable,
      enabled: targetWidget.enabled,
      internalId: targetWidget.internalId,
      chapter: targetChapterSelector,
      opt: false,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: outputTargetValue,
      version: targetWidget.version,
    };

    if (this.logService.isLoggable(LogLevel.DEBUG)) {
      console.log();
      console.log('Output Trigger: ', request);
    }
    return of(request);
  }

  private preliminaryRiskFromCheckboxImpl(
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const sourceValue: string[] = sourceWidget.value;
    const targetValue: WidgetPreliminaryRiskAssessmentValueV0[] =
      targetWidget.value;
    const triggerArgs: WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs[] =
      args as WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs[];

    if (this.logService.isLoggable(LogLevel.DEBUG)) {
      console.groupCollapsed('preliminaryRiskFromTreatmentDataInterestedImpl');
      console.log('SourceValue: ', sourceValue);
      console.log('TargetValue: ', targetValue);
      console.log('Args:   ', triggerArgs);
      console.groupEnd();
    }

    let outputTargetValue: WidgetPreliminaryRiskAssessmentValueV0[] =
      targetValue || [];

    triggerArgs.forEach(
      (a: WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs) => {
        switch (a.sourceOperator) {
          case 'includeOneOrMoreRow':
            outputTargetValue = TriggerService.includeOneOrMoreRow(
              sourceValue,
              a.sourceValue || [],
              a,
              outputTargetValue,
              this.logService.isLoggable(LogLevel.DEBUG)
            );
            break;
          case 'notIncludeRow':
            outputTargetValue = TriggerService.notIncludeRow(
              sourceValue,
              a.sourceValue || [],
              a,
              outputTargetValue,
              this.logService.isLoggable(LogLevel.DEBUG)
            );
            break;
          case 'includeOneOrMoreCol':
            outputTargetValue = TriggerService.includeOneOrMoreCol(
              sourceValue,
              a.sourceValue || [],
              a,
              outputTargetValue,
              this.logService.isLoggable(LogLevel.DEBUG)
            );
            break;
          case 'notIncludeCol':
            outputTargetValue = TriggerService.notIncludeCol(
              sourceValue,
              a.sourceValue || [],
              a,
              outputTargetValue,
              this.logService.isLoggable(LogLevel.DEBUG)
            );
            break;
          default:
            if (this.logService.isLoggable(LogLevel.DEBUG)) {
              console.log(
                'Ignored Trigger: INVALID sourceOperator ' + a.sourceOperator
              );
            }
            return of(undefined);
        }
      }
    );

    const request: Widget = {
      editable: targetWidget.editable,
      enabled: targetWidget.enabled,
      internalId: targetWidget.internalId,
      chapter: targetChapterSelector,
      opt: false,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: outputTargetValue,
      version: targetWidget.version,
    };

    if (this.logService.isLoggable(LogLevel.DEBUG)) {
      console.log();
      console.log('Output Trigger: ', request);
    }
    return of(request);
  }

  private initialization(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.CONTACT_CHOOSER:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.INITIALIZATION
        );
        return this.initializationContactChooser(
          widgets,
          sourceWidget,
          args,
          sourceChapterSelector,
          queryResponse
        );
      case WidgetType.RISK_ASSESSMENT:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.INITIALIZATION
        );
        return this.initializationRiskAssessment(
          widgets,
          sourceWidget,
          args,
          sourceChapterSelector,
          queryResponse
        );
      default:
        return of(undefined);
    }
  }

  private alignCategory(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.CHECKBOX:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_CATEGORY
        );
        return this.alignCategoryRiskAssessment(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          targetChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private activeAction(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ACTIVE_ACTION
        );
        return this.activeActionSimpleRadioButton(
          widgets,
          targetWidget,
          args,
          targetChapterSelector,
          queryResponse
        );
      default:
        return of(undefined);
    }
  }

  private disableAction(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.DEACTIVATE_ACTION
        );
        return this.deactiveActionSimpleRadioButton(
          widgets,
          targetWidget,
          args,
          targetChapterSelector,
          queryResponse
        );
      default:
        return of(undefined);
    }
  }

  private upsertValue(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.TREATMENT_DATA_INTERESTED:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.UPSERT_VALUE
        );
        return this.upsertValueWTreatmentDataInterested2WDRD(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      case WidgetType.CHECKBOX:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.UPSERT_VALUE
        );
        return this.upsertValueWCheckbox2WDRD(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private appValue(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.CHECKBOX:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.APP_VALUE
        );
        return this.appValueAppSummary(
          sourceWidget,
          widgets,
          targetWidget,
          args,
          targetChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private initializationContactChooser(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    const value = WidgetUtils.mappingQueryResponse(
      queryResponse,
      args?.query?.mapping
    ) as WidgetContactChooserValueV0Dto[];
    const request: Widget = {
      editable: sourceWidget.editable,
      internalId: sourceWidget.internalId,
      chapter: sourceChapterSelector,
      opt: false,
      properties: sourceWidget.properties,
      type: sourceWidget.type,
      value: value,
      version: sourceWidget.version,
      enabled: sourceWidget.enabled,
    };
    return of(request);
  }

  private initializationRiskAssessment(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector,
    queryResponse?: any
  ): Observable<Widget | undefined> {
    const queryAnswers = WidgetUtils.mappingQueryResponse(
      queryResponse,
      args?.query?.mapping
    ) as WRAQueryGlobalMeasureCheckValue[];
    const newAnswers: WRAValueAnswer[] = queryAnswers.map((v) => {
      return {
        uuid: v.uuid,
      };
    });
    const value: WraValue = {
      answers: newAnswers,
    };
    const request: Widget = {
      editable: sourceWidget.editable,
      internalId: sourceWidget.internalId,
      chapter: sourceChapterSelector,
      opt: false,
      properties: sourceWidget.properties,
      type: sourceWidget.type,
      value: value,
      version: sourceWidget.version,
      enabled: sourceWidget.enabled,
    };
    return of(request);
  }

  private enableLia(sourceWidget: UiWidget): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.CHECKBOX:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ENABLE_LIA
        );
        return this.enableLiaImpl(sourceWidget);
      default:
        return of(undefined);
    }
  }

  private alignSubResponsible(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_SUB_RESPONSIBLE
        );
        return this.alignSubResponsibleImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private enableLiaImpl(
    sourceWidget: UiWidget
  ): Observable<Widget | undefined> {
    const values: string[] = sourceWidget.value;
    const itemLIAActivation: string | undefined =
      sourceWidget.args?.itemLIAActivation;
    if (itemLIAActivation && values && values.includes(itemLIAActivation)) {
      this.triggerStoreService.activeChapter(ChapterSelector.TREAT_LIA);
    } else {
      this.triggerStoreService.setDisableWarning(TreatmentWarning.LI_SELECTED);
    }
    return of(undefined);
  }

  private alignSubResponsibleImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: number = sourceWidget.value;
    let opt: boolean;
    let enabled: boolean;
    if (currentWidgetValue === 0) {
      // Registro Titolare
      enabled = false;
      opt = false;
    } else {
      // Registro Responsabile
      enabled = true;
      opt = false;
    }

    const request: Widget = {
      editable: targetWidget.editable,
      enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: targetWidget.value,
      version: targetWidget.version,
    };
    return of(request);
  }

  private alignRepresentativeOfHolder(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_REPRESENTATIVE_OF_HOLDER
        );
        return this.alignRepresentativeOfHolderImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private alignRepresentativeOfHolderImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: number = sourceWidget.value;

    let opt: boolean;
    let enabled: boolean;

    if (currentWidgetValue === 0) {
      // Resposabile
      enabled = true;
      opt = false;
    } else {
      // Sub Responsabile
      enabled = false;
      opt = false;
    }

    const request: Widget = {
      editable: targetWidget.editable,
      enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: targetWidget.value,
      version: targetWidget.version,
    };

    return of(request);
  }

  private alignHolder(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_HOLDER
        );
        return this.alignHolderImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private alignHolderImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: number = sourceWidget.value;

    let opt: boolean;
    let enabled: boolean;

    if (currentWidgetValue === 0) {
      // Resposabile
      enabled = false;
      opt = false;
    } else {
      // Sub Responsabile
      enabled = true;
      opt = true;
    }

    const request: Widget = {
      editable: targetWidget.editable,
      enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: targetWidget.value,
      version: targetWidget.version,
    };

    return of(request);
  }

  private alignRepresentativeOfResponsible(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_REPRESENTATIVE_OF_RESPONSIBLE
        );
        return this.alignRepresentativeOfResponsibleImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private alignRepresentativeOfResponsibleImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: number = sourceWidget.value;

    let opt: boolean;
    let enabled: boolean;

    if (currentWidgetValue === 0) {
      // Resposabile
      enabled = true;
      opt = true;
    } else {
      // Sub Responsabile
      enabled = false;
      opt = false;
    }

    const request: Widget = {
      editable: targetWidget.editable,
      enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: targetWidget.value,
      version: targetWidget.version,
    };
    return of(request);
  }

  private alignResponsible(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_RESPONSIBLE
        );
        return this.alignResponsibleImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private alignResponsibleImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: number = sourceWidget.value;

    let opt: boolean;
    let enabled: boolean;

    if (currentWidgetValue === 0) {
      // Resposabile
      enabled = false;
      opt = false;
    } else {
      // Sub Responsabile
      enabled = true;
      opt = false;
    }

    const request: Widget = {
      editable: targetWidget.editable,
      enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: targetWidget.value,
      version: targetWidget.version,
    };

    return of(request);
  }

  private alignResponsibleMe(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.SIMPLE_RADIO_BUTTON:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.ALIGN_RESPONSIBLE_ME
        );
        return this.alignResponsibleMeImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private alignResponsibleMeImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: number = sourceWidget.value;

    let opt: boolean;
    let enabled: boolean;

    if (currentWidgetValue === 0) {
      // Resposabile
      enabled = true;
      opt = false;
    } else {
      // Sub Responsabile
      enabled = false;
      opt = false;
    }

    const request: Widget = {
      editable: targetWidget.editable,
      enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: targetWidget.value,
      version: targetWidget.version,
    };

    return of(request);
  }

  private upsertDataRetentionAndDeletion(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    switch (sourceWidget?.type) {
      case WidgetType.RECIPIENT_CHOOSER:
        this.validWidgetAction(
          sourceWidget,
          DocumentEditorActionsType.UPSERT_DATA_RETENTION_AND_DELETION
        );
        return this.upsertDataRetentionAndDeletionImpl(
          widgets,
          sourceWidget,
          targetWidget,
          args,
          sourceChapterSelector
        );
      default:
        return of(undefined);
    }
  }

  private upsertDataRetentionAndDeletionImpl(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const currentWidgetValue: WidgetRecipientChooserV0Value[] =
      this.getWidgetValue(widgets, sourceWidget.internalId);
    const targetWidgetValue: WidgetDataRetentionAndDeletionV0ValueDto[] =
      targetWidget.value;
    const newValue = [];
    currentWidgetValue.forEach((sv) => {
      const alreadyExits = targetWidgetValue?.find(
        (t) => t.category === sv.recipient
      );
      if (alreadyExits) {
        newValue.push(alreadyExits);
      } else {
        newValue.push({
          category: sv.recipient,
          retentionPeriod: undefined,
          retentionPeriodUnit: undefined,
          retentionPeriodOther: true,
          retentionPeriodReason: undefined,
          pseudonymization: false,
          anonymization: false,
          deletion: false,
          note: undefined,
        } as WidgetDataRetentionAndDeletionV0ValueDto);
      }
    });
    const request: Widget = {
      editable: targetWidget.editable,
      enabled: targetWidget.enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt: false,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: newValue,
      version: targetWidget.version,
    };
    return of(request);
  }

  private upsertValueWTreatmentDataInterested2WDRD(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    // const uuids: string[] = sourceWidget.value?.row || [];

    const uuids: string[] = WidgetActionsUtils.execJsonPath(
      args?.jsonPath?.source,
      widgets
    );
    const newValue: WidgetDataRetentionAndDeletionV0ValueDto[] = []; //currentWidgetValue?.filter((r: WidgetDataRetentionAndDeletionV0PreviewValueDto) => uuids.includes(r.category));
    const targetValue: WidgetDataRetentionAndDeletionV0ValueDto[] =
      targetWidget.value;
    uuids.forEach((sv) => {
      if (newValue && !newValue?.find((data) => data.category === sv)) {
        const alreadyExits = targetValue?.find((v) => v.category === sv);
        if (alreadyExits) {
          newValue.push(alreadyExits);
        } else {
          newValue.push({
            category: sv,
            retentionPeriod: undefined,
            retentionPeriodUnit: undefined,
            retentionPeriodOther: true,
            retentionPeriodReason: undefined,
            pseudonymization: false,
            anonymization: false,
            deletion: false,
            note: undefined,
          } as WidgetDataRetentionAndDeletionV0ValueDto);
        }
      }
    });
    const request: Widget = {
      editable: targetWidget.editable,
      enabled: targetWidget.enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt: false,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: newValue,
      version: targetWidget.version,
    };
    return of(request);
  }

  private upsertValueWCheckbox2WDRD(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    sourceChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    const uuids: string[] = WidgetActionsUtils.execJsonPath(
      args?.jsonPath?.source,
      widgets
    );
    // const currentWidgetValue = this.getWidgetValue(widgets, sourceWidget.internalId);
    const newValue: WidgetDataRetentionAndDeletionV0ValueDto[] = []; //currentWidgetValue?.filter((r: WidgetDataRetentionAndDeletionV0PreviewValueDto) => uuids.includes(r.category));
    const targetValue: WidgetDataRetentionAndDeletionV0ValueDto[] =
      targetWidget.value;
    uuids.forEach((sv) => {
      if (newValue && !newValue?.find((data) => data.category === sv)) {
        const alreadyExits = targetValue?.find((v) => v.category === sv);
        if (alreadyExits) {
          newValue.push(alreadyExits);
        } else {
          newValue.push({
            category: sv,
            retentionPeriod: undefined,
            retentionPeriodUnit: undefined,
            retentionPeriodOther: true,
            retentionPeriodReason: undefined,
            pseudonymization: false,
            anonymization: false,
            deletion: false,
            note: undefined,
          } as WidgetDataRetentionAndDeletionV0ValueDto);
        }
      }
    });
    const request: Widget = {
      editable: targetWidget.editable,
      enabled: targetWidget.enabled,
      internalId: targetWidget.internalId,
      chapter: sourceChapterSelector,
      opt: false,
      properties: targetWidget.properties,
      type: targetWidget.type,
      value: newValue,
      version: targetWidget.version,
    };
    return of(request);
  }

  private alignCategoryRiskAssessment(
    widgets: UiWidget[],
    sourceWidget: UiWidget,
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    return new Observable((observer) => {
      const sourceValue: string[] = sourceWidget.value || [];
      this.queryService.resolve(args.query).subscribe(
        (queryResponse: unknown[]) => {
          const mapping = args.query.mapping;
          const mappedResponse: AsyncTriggerQueryResponse[] =
            WidgetUtils.mappingQueryResponse(
              queryResponse,
              mapping
            ) as AsyncTriggerQueryResponse[];
          const validResponse: AsyncTriggerQueryResponse[] =
            mappedResponse.filter((r) => sourceValue.includes(r.categoryUUID));
          const targetValue: WraValue = targetWidget.value;
          const targetAnswers: WRAValueAnswer[] = targetValue?.answers || [];
          const answers: WRAValueAnswer[] = [];
          validResponse.forEach((r) => {
            const targetCurrentAnsware = targetAnswers?.find(
              (t) => t.uuid === r.uuid
            );
            if (targetCurrentAnsware) {
              answers.push(targetCurrentAnsware);
            } else {
              answers.push({
                uuid: r.uuid,
              });
            }
          });
          // rimuovo il global measure check nel caso non ho più answare
          const requestValue: WraValue = {
            globalMeasureCheckDocId:
              answers.length > 0
                ? targetValue.globalMeasureCheckDocId
                : undefined,
            answers,
          };
          const request: Widget = {
            internalId: targetWidget?.internalId,
            chapter: targetChapterSelector,
            version: targetWidget.version,
            type: targetWidget?.type,
            value: requestValue,
            properties: targetWidget?.properties,
            editable: targetWidget?.editable,
            opt: false,
            enabled: targetWidget?.enabled,
          };

          this.triggerStoreService.setDisableWarning(
            TreatmentWarning.GLOBAL_MEAS_LIST_CHANGED
          );

          observer.next(request);
          observer.complete();
        },
        () => {
          observer.next(undefined);
          observer.complete();
        }
      );
    });
  }

  private activeActionSimpleRadioButton(
    widgets: UiWidget[],
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector,
    queryResponse: any
  ): Observable<Widget | undefined> {
    console.log('activeActionSimpleRadioButton');

    const response: any[] = queryResponse as any[];
    const sourceValue: number[] = WidgetActionsUtils.execJsonPath(
      args?.jsonPath?.source,
      widgets
    );
    if (sourceValue?.length > 0 && args?.jsonPath?.expectedValue?.length > 0) {
      const expectedValue = args?.jsonPath?.expectedValue;
      let valid = true;
      sourceValue.forEach((f) => {
        if (!expectedValue?.includes(f)) {
          valid = false;
        }
      });

      let uuid: string = null;
      if (response?.length === 1) {
        uuid = response[0]?.uuid;
      }

      if (!uuid) {
        return of(undefined);
      }

      console.log('activeActionSimpleRadioButton valid ', valid);

      if (valid) {
        const widgetValue: WidgetPreliminaryRiskAssessmentValueV0[] =
          targetWidget.value.filter((w) => w.uuid !== uuid);
        widgetValue.push({ uuid, selected: true, editable: false }); // quando ripristinato andrebbe messo a false
        /**
         * La propagazione avviene solo se applico la modifica di editable da false a true.
         */
        const request: Widget = {
          internalId: targetWidget?.internalId,
          chapter: targetChapterSelector,
          version: targetWidget.version,
          type: targetWidget?.type,
          value: widgetValue,
          properties: targetWidget.properties,
          editable: targetWidget?.editable,
          opt: false,
          enabled: targetWidget?.enabled,
        };
        return of(request);
      } else {
        return of(undefined);
      }
    }
    return of(undefined);
  }

  private deactiveActionSimpleRadioButton(
    widgets: UiWidget[],
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector,
    queryResponse: any
  ): Observable<Widget | undefined> {
    /**
     * Senza editable questo trigger fa danni, perchè viene sempre eseguito
     */
    console.log('deactiveActionSimpleRadioButton');
    const response: any[] = queryResponse as any[];
    const sourceValue: number[] = WidgetActionsUtils.execJsonPath(
      args?.jsonPath?.source,
      widgets
    );
    console.log('deactiveActionSimpleRadioButton sourceValue ', sourceValue);
    if (args?.jsonPath?.expectedValue?.length > 0) {
      const expectedValue = args?.jsonPath?.expectedValue;
      let valid = true;
      if (sourceValue && sourceValue.length > 0) {
        sourceValue.forEach((f) => {
          if (!expectedValue?.includes(f)) {
            valid = false;
          }
        });
      }


      let uuid: string = null;
      if (response?.length === 1) {
        uuid = response[0]?.uuid;
      }

      if (!uuid) {
        return null;
      }
      console.log('deactiveActionSimpleRadioButton valid ', valid);

      if (valid) {

        const widgetValue: WidgetPreliminaryRiskAssessmentValueV0[] =
          targetWidget.value.filter((w) => w.uuid !== uuid);
        widgetValue.push({ uuid, selected: false, editable: false }); // quando

        const request: Widget = {
          internalId: targetWidget?.internalId,
          chapter: targetChapterSelector,
          version: targetWidget.version,
          type: targetWidget?.type,
          value: widgetValue,
          properties: targetWidget.properties,
          editable: targetWidget?.editable,
          opt: false,
          enabled: targetWidget?.enabled,
        };
        return of(request);
      } else {
        return of(undefined);
      }
    }
    return of(undefined);
  }

  private appValueAppSummary(
    sourceWidget: UiWidget,
    widgets: UiWidget[],
    targetWidget: UiWidget,
    args: TemplateActionArgs,
    targetChapterSelector: ChapterSelector
  ): Observable<Widget | undefined> {
    return new Observable((observer) => {
      const sourceValue: string[] = sourceWidget.value;
      // const sourceValue: string[] = WidgetActionsUtils.execJsonPath(args?.jsonPath?.source, widgets);
      /*
       ["tes2302211252xke","wha2307270822yoa"]

        Confidentiality -> R
        Integrity -> I
        Availability -> D
       */

      if (sourceValue?.length > 0) {
        this.applicationService.findAll().subscribe(
          (s: Application[]) => {
            const selectedApp: Application[] = s.filter((app) =>
              sourceValue.includes(app.uuid)
            );
            const targetValue: WidgetAppSummaryV0ValueDto = targetWidget.value
              ? JSON.parse(JSON.stringify(targetWidget.value))
              : { app: [] };
            targetValue.app = [];
            targetValue.app = selectedApp.map((a: Application) => {
              const appAlreadySaved = targetValue.app?.find(
                (ta) => ta.applicationUuid === a.uuid
              );
              if (appAlreadySaved) {
                return appAlreadySaved;
              } else {
                const response: WidgetAppSummaryV0ValueAppDto = {
                  applicationUuid: a.uuid,
                  unsupported:
                    a.assessmentCategory === AssessmentCategory.NOT_SUPPORTED,
                  scenarioRLevel: a.confidentialityViolationLevel,
                  scenarioILevel: a.integrityCompromiseLevel,
                  scenarioDLevel: a.availabilityLossLevel,
                  scenarioRImplValue: a.confidentialityViolationImplValue,
                  scenarioIImplValue: a.integrityCompromiseImplValue,
                  scenarioDImplValue: a.availabilityLossImplValue,
                };
                return response;
              }
            });
            targetWidget.value = targetValue;
            this.triggerStoreService.setDisableWarning(
              TreatmentWarning.APP_LIST_CHANGED
            );
            const request: Widget = {
              internalId: targetWidget?.internalId,
              version: targetWidget.version,
              type: targetWidget.type,
              value: targetValue,
              properties: targetWidget.properties,
              editable: targetWidget.editable,
              enabled: targetWidget.enabled,
              opt: false,
              chapter: targetChapterSelector,
            };
            observer.next(request);
            observer.complete();
          },
          () => {
            const targetValue: WidgetAppSummaryV0ValueDto = targetWidget.value;
            targetValue.app = [];
            this.triggerStoreService.setDisableWarning(
              TreatmentWarning.APP_LIST_CHANGED
            );

            const request: Widget = {
              internalId: targetWidget?.internalId,
              version: targetWidget.version,
              type: targetWidget.type,
              value: targetValue,
              properties: targetWidget.properties,
              editable: targetWidget.editable,
              enabled: targetWidget.enabled,
              opt: false,
              chapter: targetChapterSelector,
            };
            observer.next(request);
            observer.complete();
          }
        );
      } else {
        const targetValue: WidgetAppSummaryV0ValueDto = targetWidget.value;
        targetValue.app = [];
        this.triggerStoreService.setDisableWarning(
          TreatmentWarning.APP_LIST_CHANGED
        );
        const request: Widget = {
          internalId: targetWidget?.internalId,
          version: targetWidget.version,
          type: targetWidget.type,
          value: targetValue,
          properties: targetWidget.properties,
          editable: targetWidget.editable,
          enabled: targetWidget.enabled,
          opt: false,
          chapter: targetChapterSelector,
        };
        observer.next(request);
        observer.complete();
      }
    });
  }

  private static mergeList(
    actualValue: WidgetPreliminaryRiskAssessmentValueV0[],
    targetValue: WidgetPreliminaryRiskAssessmentValueV0[]
  ) {
    const targetUUIDs = new Set(targetValue.map((t) => t.uuid));
    // Filtra solo gli elementi non sovrascritti
    const merged = actualValue.filter((item) => !targetUUIDs.has(item.uuid));
    // Aggiunge i nuovi/aggiornati
    merged.push(...targetValue);
    return merged;
  }

  /**
   * Il trigger si applica se uno o più elementi inseriti in triggerArgs?.sourceValue?.row
   * sono stati selezionati quindi presenti in sourceValue?.row
   * NON si applica se uno dei due insiemi è vuoto
   * @param sourceValueUUID
   * @param targetValueUUID
   * @param triggerArgs
   * @param outputTargetValue
   * @param log
   * @private
   */
  public static includeOneOrMoreRow(
    sourceValueUUID: string[],
    targetValueUUID: string[],
    triggerArgs:
      | WidgetPreliminaryRiskAssessmentTriggerArgsV0
      | WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs,
    outputTargetValue: WidgetPreliminaryRiskAssessmentValueV0[],
    log: boolean
  ) {
    let logTitle = '';
    if (sourceValueUUID.length === 0 || targetValueUUID.length === 0) {
      logTitle = '[IGNORED] includeOneOrMoreRow. Empty set';
    } else {
      if (this.includeOneOrMore(sourceValueUUID, targetValueUUID)) {
        logTitle = '[APPLIED] includeOneOrMoreRow.';
        outputTargetValue = this.mergeList(
          outputTargetValue || [],
          triggerArgs.targetValue || []
        );
      } else {
        logTitle = '[IGNORED] includeOneOrMoreRow. Not found element';
      }
    }

    if (log) {
      console.groupCollapsed(logTitle);
      if (triggerArgs.info) {
        console.log('Info ', triggerArgs.info);
      }
      console.log('sourceValueUUID         ', sourceValueUUID);
      console.log('targetValueUUID         ', targetValueUUID);
      console.log('outputTargetValue       ', outputTargetValue);
      console.groupEnd();
    }
    return outputTargetValue;
  }

  /**
   * Il trigger si applica se uno o più elementi inseriti in triggerArgs?.sourceValue?.col
   * sono stati selezionati quindi presenti in sourceValue?.col
   * NON si applica se uno dei due insiemi è vuoto
   * @param sourceValueUUID
   * @param targetValueUUID
   * @param triggerArgs
   * @param outputTargetValue
   * @param log
   */
  public static includeOneOrMoreCol(
    sourceValueUUID: string[],
    targetValueUUID: string[],
    triggerArgs:
      | WidgetPreliminaryRiskAssessmentTriggerArgsV0
      | WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs,
    outputTargetValue: WidgetPreliminaryRiskAssessmentValueV0[],
    log: boolean
  ) {
    let logTitle = '';
    if (sourceValueUUID.length === 0 || targetValueUUID.length === 0) {
      logTitle = '[IGNORED] includeOneOrMoreCol. Empty set';
    } else {
      if (this.includeOneOrMore(sourceValueUUID, targetValueUUID)) {
        logTitle = '[APPLIED] includeOneOrMoreCol.';
        outputTargetValue = this.mergeList(
          outputTargetValue || [],
          triggerArgs.targetValue || []
        );
      } else {
        logTitle = '[IGNORED] includeOneOrMoreCol. Not found element';
      }
    }

    if (log) {
      console.groupCollapsed(logTitle);
      if (triggerArgs.info) {
        console.log('Info ', triggerArgs.info);
      }
      console.log('sourceValueUUID          ', sourceValueUUID);
      console.log('targetValueUUID          ', targetValueUUID);
      console.log('outputTargetValue        ', outputTargetValue);
      console.groupEnd();
    }
    return outputTargetValue;
  }

  /**
   * Il trigger si applica se nessuno degli elementi inseriti in triggerArgs?.sourceValue?.row
   * sono stati selezionati quindi presenti in sourceValue?.row
   * NON si applica se uno dei due insiemi è vuoto
   * @param sourceValueUUID
   * @param targetValueUUID
   * @param triggerArgs
   * @param outputTargetValue
   * @param log
   * @private
   */
  public static notIncludeRow(
    sourceValueUUID: string[],
    targetValueUUID: string[],
    triggerArgs:
      | WidgetPreliminaryRiskAssessmentTriggerArgsV0
      | WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs,
    outputTargetValue: WidgetPreliminaryRiskAssessmentValueV0[],
    log: boolean
  ) {
    let logTitle = '';
    if (targetValueUUID.length === 0) {
      logTitle = '[IGNORED] notIncludeRow. Empty set';
    } else {
      if (this.notInclude(sourceValueUUID, targetValueUUID)) {
        logTitle = '[APPLIED] notIncludeRow.';
        outputTargetValue = this.mergeList(
          outputTargetValue || [],
          triggerArgs.targetValue || []
        );
      } else {
        logTitle = '[IGNORED] notIncludeRow. Not found element';
      }
    }

    if (log) {
      console.groupCollapsed(logTitle);
      if (triggerArgs.info) {
        console.log('Info ', triggerArgs.info);
      }
      console.log('sourceValueUUID   ', sourceValueUUID);
      console.log('targetValueUUID   ', targetValueUUID);
      console.log('outputTargetValue ', outputTargetValue);
      console.groupEnd();
    }
    return outputTargetValue;
  }

  /**
   * Il trigger si applica se nessuno degli elementi inseriti in triggerArgs?.sourceValue?.col
   * sono stati selezionati quindi presenti in sourceValue?.col
   * NON si applica se uno dei due insiemi è vuoto
   * @param sourceValueUUID
   * @param targetValueUUID
   * @param triggerArgs
   * @param outputTargetValue
   * @param log
   * @private
   */
  public static notIncludeCol(
    sourceValueUUID: string[],
    targetValueUUID: string[],
    triggerArgs:
      | WidgetPreliminaryRiskAssessmentTriggerArgsV0
      | WidgetPreliminaryRiskAssessmentFromCheckboxTriggerArgs,
    outputTargetValue: WidgetPreliminaryRiskAssessmentValueV0[],
    log: boolean
  ) {
    let logTitle = '';
    if (targetValueUUID.length === 0) {
      logTitle = '[IGNORED] targetValueUUID. Empty set';
    } else {
      if (this.notInclude(sourceValueUUID, targetValueUUID)) {
        logTitle = '[APPLIED] notIncludeCol.';
        outputTargetValue = this.mergeList(
          outputTargetValue || [],
          triggerArgs.targetValue || []
        );
      } else {
        logTitle = '[IGNORED] notIncludeCol. Not found element';
      }
    }

    if (log) {
      console.groupCollapsed(logTitle);
      if (triggerArgs.info) {
        console.log('Info ', triggerArgs.info);
      }
      console.log('sourceValueUUID    ', sourceValueUUID);
      console.log('targetValueUUID    ', targetValueUUID);
      console.log('outputTargetValue  ', outputTargetValue);
      console.groupEnd();
    }
    return outputTargetValue;
  }

  private static includeOneOrMore(
    sourceUUIDs: string[],
    targetUUIDs: string[]
  ) {
    return sourceUUIDs.some((r) => targetUUIDs.includes(r));
  }

  private static notInclude(sourceUUIDs: string[], targetUUIDs: string[]) {
    return (
      targetUUIDs.length === 0 || sourceUUIDs.length === 0 ||
      sourceUUIDs.filter((r) => targetUUIDs.includes(r)).length === 0
    );
  }
}

interface AsyncTriggerQueryResponse {
  uuid: string;
  internalId: string;
  normativeReference: string;
  category: string;
  categoryUUID: string;
  description: string;
  help: string;
  unauthorizedAccess: string;
  unauthorizedDisclosure: string;
  unauthorizedUpdate: string;
  loss: string;
  accidentalOrIllegalDestruction: string;
  improperUse: string;
  checkValue: string;
}
